// Form state + submit logic for the "reader's card" (name + age →
// create learner → jump into placement). Extracted out of
// ReaderCardSpread so the component stays presentation-only.
//
// Grade is derived from age (US convention, age − 5, clamped to K) since
// the card deliberately asks only name + age.

import { useState } from 'react'
import { useCreateLearner } from '@/hooks/useLearners'
import { useGlobalState } from '@/stores/globalState'

export const AGE_STAMPS = ['3', '4', '5', '6', '7', '8', '9+'] as const

function ageToNumber(stamp: string): number {
  return stamp === '9+' ? 9 : parseInt(stamp, 10)
}

function gradeFromAge(age: number): number {
  return Math.max(0, age - 5)
}

export function useReaderCardForm(onCreated?: () => void) {
  const [name, setName] = useState('')
  const [ageStamp, setAgeStamp] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const createLearner = useCreateLearner()
  const setActiveLearner = useGlobalState((s) => s.setActiveLearner)
  const setActiveModule = useGlobalState((s) => s.setActiveModule)
  const setActiveWorkspaceItem = useGlobalState((s) => s.setActiveWorkspaceItem)
  const setWorkspaceView = useGlobalState((s) => s.setWorkspaceView)

  const ready = name.trim().length > 0 && ageStamp !== null
  const kid = name.trim() || 'your reader'

  const handleSubmit = async () => {
    if (!ready || createLearner.isPending) return
    setError(null)
    const ageNum = ageToNumber(ageStamp!)
    try {
      const learner = await createLearner.mutateAsync({
        name: name.trim(),
        age: ageNum,
        grade: gradeFromAge(ageNum),
      })
      setActiveLearner(learner.id, learner)
      onCreated?.()
      // The CTA names the payoff: straight into the placement. The
      // 'evaluation-flow' route auto-starts the wizard (no second welcome
      // ask — this button already collected intent).
      setActiveModule('EVALUATION')
      setActiveWorkspaceItem('evaluation-flow')
      setWorkspaceView('FOCUSED')
    } catch {
      setError('That did not save. Please try again.')
    }
  }

  return {
    name,
    setName,
    ageStamp,
    setAgeStamp,
    error,
    ready,
    kid,
    isPending: createLearner.isPending,
    handleSubmit,
  }
}
